import { inject, Injectable } from '@angular/core';
import { PNLPivotGridsStateKey } from '@approot/models';
import { Header } from '@approot/models/ag-grid.model';
import { RunningState } from '@approot/positions-detail/shared/utils';
import {
  AgGridService,
  ConfigService,
  UserStorageService
} from '@approot/services';
import { ColDef, ColumnState } from 'ag-grid-community';
import { cloneDeep, isEqual } from 'lodash';
import { Observable, ReplaySubject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { setColumnsColors } from './pnl-pivot.funcs';
import { PnlPivotGrid, PNLPivotState } from './pnl-pivot.types';

const DefaultPNLPivotState: PNLPivotState = {
  uuid: null,
  rows: [],
  runningState: RunningState.Idle,
  info: null
};

@Injectable({
  providedIn: 'root'
})
export class PNLPivotService {
  public stateChanged: Observable<PNLPivotState>;
  private state: PNLPivotState = {};
  private state$ = new ReplaySubject<PNLPivotState>(1);
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);
  private agGridService = inject(AgGridService);

  constructor() {
    const savedGrids = this.userStorageService.getItem<PnlPivotGrid[]>(
      PNLPivotGridsStateKey
    );
    this.setState({
      ...DefaultPNLPivotState,
      entities: [],
      headers: [],
      grids: savedGrids ? cloneDeep(savedGrids) : []
    });
    this.stateChanged = this.state$.asObservable();
    this.loadPNLPivotBlotterHeader();
  }

  public update(state: PNLPivotState): void {
    this.setState(state);
  }

  public displayPnlPivotBlotter(): void {
    const newGrid: PnlPivotGrid = {
      columnState: this.getPreviousPnlPivotGridState()
    };
    const grids = [...this.state.grids, newGrid];
    this.update({ grids });
  }

  public removePnlPivotGrid(index: number): void {
    const grids = this.state.grids;
    grids.splice(index, 1);
    this.update({ grids });
    this.userStorageService.setItem(PNLPivotGridsStateKey, cloneDeep(grids));
  }

  public savePnlPivotGridState(index: number, columnState: ColumnState[]) {
    const grids = this.state.grids;
    grids[index].columnState = columnState;
    this.update({ grids });
    this.userStorageService.setItem(PNLPivotGridsStateKey, cloneDeep(grids));
  }

  public getPreviousPnlPivotGridState(): ColumnState[] {
    const updatedGrid = this.state.grids[this.state.grids.length - 1];
    return updatedGrid?.columnState || [];
  }

  /****<PRIVATE>**************************************************************/

  private loadPNLPivotBlotterHeader(): void {
    this.configService
      .getAgGridConfig()
      .pipe(
        take(1),
        map(config => {
          const pnlPivotHeaders = cloneDeep(
            config.find(x => x.identifier === 'pnlBlotterSummaryViewHeaders')
              .headers
          ) as Header[];

          const formattedHeaders = this.agGridService.formatHeaders(
            pnlPivotHeaders
          ) as ColDef[];

          setColumnsColors(formattedHeaders);
          return formattedHeaders;
        })
      )
      .subscribe({
        next: headers => this.setState({ headers })
      });
  }

  private setState(state: PNLPivotState, emit = true): void {
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }
    this.state = newState;
    if (emit) {
      this.state$.next(this.state);
    }
  }
}
